import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Divider,
  Grid,
  Group,
  Stack,
  Text,
  Textarea,
  TextInput,
  Modal,
  Paper,
  Select,
  Checkbox,
  Badge,
  Pagination,
  Center,
  ActionIcon,
  Tooltip,
  Box
} from '@mantine/core';

import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { IconDeviceFloppy, IconSearch, IconTrash, IconPlus } from '@tabler/icons-react';
import { useMutation, useQueryClient, useQuery, keepPreviousData } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import SegmentedInput from 'components/SegmentedInput';
import DataTables from 'components/DataTables';
import DateField from 'components/DateField';
import { parseLocalDate, toLocalDateString } from 'utils/localDate';
import React, { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { pathnames } from 'routes/lcms';
import { coursePlanSchema, useCoursePlanStore, CoursePlanFormData } from 'store/createCoursePlan';
import {
  coursePlanService,
  CreateCoursePlanDto,
  UpdateCoursePlanDto,
  CourseModularData,
  CourseProgramData,
  DACUMProgramData,
  CourseEmergingNeedsData
} from 'services/lcms/coursePlanService';
import type {
  BusinessUnit,
  CoursePlanBusinessUnitForm,
  CoursePlanJobFamilyGroupForm,
  CoursePlanJobFamilyForm,
  JobFamilyGroup,
  JobFamily,
  CoursePlanTargetPositionForm
} from 'types/lcms/coursePlan';
import BusinessUnitTable from 'modules/pages/lcms/coursePlan/BusinessUnitTable';
import JobFamilyGroupTable from 'modules/pages/lcms/coursePlan/JobFamilyGroupTable';
import JobFamilyTable from 'modules/pages/lcms/coursePlan/JobFamilyTable';
import { MODE_OF_DELIVERY } from 'constant/course';

interface CourseItem {
  courseId: string;
  courseName: string;
  courseCode?: string;
  deliveryType?: string;
  learningHours?: number;
  category?: string;
  description?: string;
  modeOfDelivery?: string;
  businessUnits?: string[];
  jobFamilyGroups?: string[];
  jobFamilies?: string[];
  targetPositions?: string[];
}

type ModalType =
  | 'courses'
  | 'businessUnits'
  | 'jobFamilyGroups'
  | 'jobFamilies'
  | 'targetPositions';
type CourseType = 'modular' | 'program' | 'emerging needs' | 'dacum';

const CoursePlanForm = () => {
  const params = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { formData, mode, setFormData, setFormMode } = useCoursePlanStore();
  const { id } = params;

  // Modal state
  const [opened, { open, close }] = useDisclosure(false);
  const [modalType, setModalType] = useState<ModalType>('courses');
  const [selectedTargetPositionIds, setSelectedTargetPositionIds] = useState<Set<string>>(
    new Set()
  );

  // Course selection state
  const [searchValue, setSearchValue] = useState<string>('');
  const [debounced] = useDebouncedValue(searchValue, 300);
  const [courseType, setCourseType] = useState<CourseType>('modular');
  const [selectedCourseIds, setSelectedCourseIds] = useState<Set<string>>(new Set());
  const [selectedBusinessUnitIds, setSelectedBusinessUnitIds] = useState<Set<string>>(new Set());
  const [selectedJobFamilyGroupIds, setSelectedJobFamilyGroupIds] = useState<Set<string>>(
    new Set()
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  const [selectedJobFamilyIds, setSelectedJobFamilyIds] = useState<Set<string>>(new Set());
  const [selectedJobFamilyGroupForFamilies, setSelectedJobFamilyGroupForFamilies] =
    useState<string>('');

  // Use ref to prevent multiple resets
  const isFormInitialized = useRef(false);
  const lastFormDataRef = useRef<string>('');

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors }
  } = useForm<CoursePlanFormData>({
    resolver: zodResolver(coursePlanSchema),
    defaultValues: formData,
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  // Sync form with store data when formData changes
  useEffect(() => {
    const formDataString = JSON.stringify(formData);

    if (formDataString !== lastFormDataRef.current && formData.planName) {
      reset(formData);
      lastFormDataRef.current = formDataString;
      isFormInitialized.current = true;
    }
  }, [formData, reset]);

  // Watch form values
  const watchedCourses = watch('courses');
  const watchedTargetPositions = watch('targetPositions');

  const getTargetPositionKey = useCallback((position: any) => {
    return (
      position?.positionId ||
      position?.positionID ||
      position?.PositionID ||
      position?.positionName ||
      position?.namaJabatan ||
      ''
    ).trim();
  }, []);

  const getTargetPositionName = useCallback((position: any) => {
    return (position?.positionName || position?.namaJabatan || position?.PositionName || '').trim();
  }, []);

  const normalizeTargetPositionDependencyValue = useCallback((value: any) => {
    return typeof value === 'string' ? value.trim() : '';
  }, []);

  const getTargetPositionBusinessUnitIds = useCallback(
    (position: any) => {
      const rawValue =
        position?.businessUnitIds ||
        position?.businessUnitID ||
        position?.businessUnitId ||
        position?.buCode ||
        position?.BuCode;
      const values = Array.isArray(rawValue) ? rawValue : [rawValue];

      return values.map(normalizeTargetPositionDependencyValue).filter(Boolean);
    },
    [normalizeTargetPositionDependencyValue]
  );

  const getTargetPositionJobFamilyGroupId = useCallback(
    (position: any) =>
      normalizeTargetPositionDependencyValue(
        position?.jobFamilyGroupId || position?.jobFamilyGroupID || position?.JobFamilyGroupID
      ),
    [normalizeTargetPositionDependencyValue]
  );

  const getTargetPositionJobFamilyId = useCallback(
    (position: any) =>
      normalizeTargetPositionDependencyValue(
        position?.jobFamilyId || position?.jobFamilyID || position?.JobFamilyID
      ),
    [normalizeTargetPositionDependencyValue]
  );

  const buildTargetPositionFormValue = useCallback(
    (position: any): CoursePlanTargetPositionForm => {
      const businessUnitIds = getTargetPositionBusinessUnitIds(position);
      const jobFamilyGroupId = getTargetPositionJobFamilyGroupId(position);
      const jobFamilyId = getTargetPositionJobFamilyId(position);

      return {
        positionId: getTargetPositionKey(position),
        positionName: getTargetPositionName(position),
        ...(businessUnitIds.length > 0 ? { businessUnitIds } : {}),
        ...(jobFamilyGroupId ? { jobFamilyGroupId } : {}),
        ...(jobFamilyId ? { jobFamilyId } : {})
      };
    },
    [
      getTargetPositionBusinessUnitIds,
      getTargetPositionJobFamilyGroupId,
      getTargetPositionJobFamilyId,
      getTargetPositionKey,
      getTargetPositionName
    ]
  );
  const watchedBusinessUnits = watch('businessUnits');
  const watchedJobFamilyGroups = watch('jobFamilyGroups');
  const watchedProgramType = watch('programType');

  const watchedJobFamilies = watch('jobFamilies');
  const jobFamilies = React.useMemo(() => watchedJobFamilies || [], [watchedJobFamilies]);

  // Memoize watched values
  const courses = React.useMemo(() => watchedCourses || [], [watchedCourses]);
  const targetPositions = React.useMemo(
    () => watchedTargetPositions || [],
    [watchedTargetPositions]
  );
  const selectedTargetPositionKeys = React.useMemo(
    () => targetPositions.map((pos) => getTargetPositionKey(pos)).filter(Boolean),
    [targetPositions, getTargetPositionKey]
  );
  const businessUnits = React.useMemo(() => watchedBusinessUnits || [], [watchedBusinessUnits]);
  const jobFamilyGroups = React.useMemo(
    () => watchedJobFamilyGroups || [],
    [watchedJobFamilyGroups]
  );
  const programType = React.useMemo(() => watchedProgramType || '', [watchedProgramType]);

  // Set initial course type based on program type
  useEffect(() => {
    if (programType) {
      setCourseType(programType.toLowerCase() as CourseType);
    }
  }, [programType]);

  // Update selected IDs when arrays change
  useEffect(() => {
    const ids = new Set(courses.map((course) => course.courseId));
    setSelectedCourseIds(ids);
  }, [courses]);

  useEffect(() => {
    const ids = new Set(businessUnits.map((bu) => bu.businessUnitId));
    setSelectedBusinessUnitIds(ids);
  }, [businessUnits]);

  useEffect(() => {
    const ids = new Set(jobFamilyGroups.map((jfg) => jfg.jobFamilyGroupId));
    setSelectedJobFamilyGroupIds(ids);
  }, [jobFamilyGroups]);

  useEffect(() => {
    const ids = new Set(jobFamilies.map((jf) => jf.jobFamilyId));
    setSelectedJobFamilyIds(ids);
  }, [jobFamilies]);

  useEffect(() => {
    setCurrentPage(1);
  }, [courseType, debounced, modalType]);

  useEffect(() => {
    const ids = new Set(selectedTargetPositionKeys);
    setSelectedTargetPositionIds(ids);
  }, [selectedTargetPositionKeys]);

  useEffect(() => {
    if (
      opened &&
      modalType === 'jobFamilies' &&
      jobFamilyGroups.length > 0 &&
      !selectedJobFamilyGroupForFamilies
    ) {
      setSelectedJobFamilyGroupForFamilies(jobFamilyGroups[0].jobFamilyGroupId);
    }
  }, [opened, modalType, jobFamilyGroups, selectedJobFamilyGroupForFamilies]);

  const handleModalClose = useCallback(() => {
    setSearchValue('');
    setCurrentPage(1);
    close();
  }, [close]);

  const openModal = useCallback(
    (type: ModalType) => {
      setModalType(type);
      setSearchValue('');
      setCurrentPage(1);
      open();
    },
    [open]
  );

  const { data: modularCourses, isFetching: isFetchingModular } = useQuery({
    queryKey: [
      'modularCourses',
      debounced,
      currentPage,
      pageSize,
      jobFamilyGroups.map((jfg) => jfg.jobFamilyGroupId),
      jobFamilies.map((jf) => jf.jobFamilyId),
      businessUnits.map((bu) => bu.businessUnitId),
      selectedTargetPositionKeys
    ],
    queryFn: () =>
      coursePlanService.getModularCourses(
        currentPage,
        pageSize,
        debounced,
        jobFamilyGroups.map((jfg) => jfg.jobFamilyGroupId),
        jobFamilies.map((jf) => jf.jobFamilyId),
        businessUnits.map((bu) => bu.businessUnitId),
        selectedTargetPositionKeys
      ),
    enabled: courseType === 'modular' && opened && modalType === 'courses',
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData
  });

  const { data: targetPositionsData, isFetching: isFetchingTargetPositions } = useQuery({
    queryKey: [
      'targetPositions',
      debounced,
      currentPage,
      pageSize,
      businessUnits.map((bu) => bu.businessUnitId),
      jobFamilyGroups.map((jfg) => jfg.jobFamilyGroupId),
      jobFamilies.map((jf) => jf.jobFamilyId),
      mode === 'edit' ? id : undefined
    ],
    queryFn: () =>
      coursePlanService.getJobPositions(
        currentPage,
        pageSize,
        debounced,
        businessUnits.map((bu) => bu.businessUnitId),
        jobFamilyGroups.map((jfg) => jfg.jobFamilyGroupId),
        jobFamilies.map((jf) => jf.jobFamilyId),
        mode === 'edit' ? id : undefined
      ),
    enabled: opened && modalType === 'targetPositions',
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData
  });

  // Fetch program courses
  const { data: programCourses, isFetching: isFetchingProgram } = useQuery({
    queryKey: [
      'programCourses',
      debounced,
      currentPage,
      pageSize,
      jobFamilyGroups.map((jfg) => jfg.jobFamilyGroupId),
      jobFamilies.map((jf) => jf.jobFamilyId),
      businessUnits.map((bu) => bu.businessUnitId),
      selectedTargetPositionKeys
    ],
    queryFn: () =>
      coursePlanService.getProgramCourses(
        currentPage,
        pageSize,
        debounced,
        jobFamilyGroups.map((jfg) => jfg.jobFamilyGroupId),
        jobFamilies.map((jf) => jf.jobFamilyId),
        businessUnits.map((bu) => bu.businessUnitId),
        selectedTargetPositionKeys
      ),
    enabled: courseType === 'program' && opened && modalType === 'courses',
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData
  });

  // Fetch DACUM programs
  const { data: dacumPrograms, isFetching: isFetchingDacum } = useQuery({
    queryKey: [
      'dacumPrograms',
      debounced,
      currentPage,
      pageSize,
      jobFamilyGroups.map((jfg) => jfg.jobFamilyGroupId),
      jobFamilies.map((jf) => jf.jobFamilyId),
      businessUnits.map((bu) => bu.businessUnitId),
      selectedTargetPositionKeys
    ],
    queryFn: () =>
      coursePlanService.getDACUMPrograms(
        currentPage,
        pageSize,
        debounced,
        jobFamilyGroups.map((jfg) => jfg.jobFamilyGroupId),
        jobFamilies.map((jf) => jf.jobFamilyId),
        businessUnits.map((bu) => bu.businessUnitId),
        selectedTargetPositionKeys
      ),
    enabled: courseType === 'dacum' && opened && modalType === 'courses',
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData
  });

  // Fetch emerging needs courses
  const { data: emergingNeedsCourses, isFetching: isFetchingEmergingNeeds } = useQuery({
    queryKey: [
      'emergingNeedsCourses',
      debounced,
      currentPage,
      pageSize,
      jobFamilyGroups.map((jfg) => jfg.jobFamilyGroupId),
      jobFamilies.map((jf) => jf.jobFamilyId),
      businessUnits.map((bu) => bu.businessUnitId),
      selectedTargetPositionKeys
    ],
    queryFn: () =>
      coursePlanService.getEmergingNeedsCourses(
        currentPage,
        pageSize,
        debounced,
        jobFamilyGroups.map((jfg) => jfg.jobFamilyGroupId),
        jobFamilies.map((jf) => jf.jobFamilyId),
        businessUnits.map((bu) => bu.businessUnitId),
        selectedTargetPositionKeys
      ),
    enabled: courseType === 'emerging needs' && opened && modalType === 'courses',
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData
  });

  // Fetch business units
  const { data: businessUnitsData, isFetching: isFetchingBusinessUnits } = useQuery({
    queryKey: ['businessUnits', debounced, currentPage, pageSize],
    queryFn: () => coursePlanService.getBusinessUnits(currentPage, pageSize, debounced),
    enabled: opened && modalType === 'businessUnits',
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData
  });

  const selectedBusinessUnitIdsForQuery = businessUnits.map((bu) => bu.businessUnitId);

  const { data: jobFamilyGroupsData, isFetching: isFetchingJobFamilyGroups } = useQuery({
    queryKey: [
      'jobFamilyGroups',
      debounced,
      currentPage,
      pageSize,
      selectedBusinessUnitIdsForQuery
    ],
    queryFn: () =>
      coursePlanService.getJobFamilyGroups(
        currentPage,
        pageSize,
        debounced,
        selectedBusinessUnitIdsForQuery
      ),
    enabled:
      opened && modalType === 'jobFamilyGroups' && selectedBusinessUnitIdsForQuery.length > 0,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData
  });

  const { data: jobFamiliesData, isFetching: isFetchingJobFamilies } = useQuery({
    queryKey: ['jobFamilies', selectedJobFamilyGroupForFamilies, debounced, currentPage, pageSize],
    queryFn: () =>
      coursePlanService.getJobFamilies(
        selectedJobFamilyGroupForFamilies,
        currentPage,
        pageSize,
        debounced
      ),
    enabled: opened && modalType === 'jobFamilies' && !!selectedJobFamilyGroupForFamilies,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData
  });

  const getCurrentData = useCallback(() => {
    switch (modalType) {
      case 'courses':
        switch (courseType) {
          case 'modular':
            return {
              items: modularCourses?.data || [],
              totalPages: modularCourses?.totalPages || 0,
              totalRecords: modularCourses?.totalRecords || 0,
              isLoading: isFetchingModular
            };
          case 'program':
            return {
              items: programCourses?.data || [],
              totalPages: programCourses?.totalPages || 0,
              totalRecords: programCourses?.totalRecords || 0,
              isLoading: isFetchingProgram
            };
          case 'dacum':
            return {
              items: dacumPrograms?.data || [],
              totalPages: dacumPrograms?.totalPages || 0,
              totalRecords: dacumPrograms?.totalRecords || 0,
              isLoading: isFetchingDacum
            };
          case 'emerging needs':
            return {
              items: emergingNeedsCourses?.data || [],
              totalPages: emergingNeedsCourses?.totalPages || 0,
              totalRecords: emergingNeedsCourses?.totalRecords || 0,
              isLoading: isFetchingEmergingNeeds
            };
          default:
            return { items: [], totalPages: 0, totalRecords: 0, isLoading: false };
        }
      case 'businessUnits':
        return {
          items: businessUnitsData?.data || [],
          totalPages: businessUnitsData?.totalPages || 0,
          totalRecords: businessUnitsData?.totalRecords || 0,
          isLoading: isFetchingBusinessUnits
        };
      case 'jobFamilyGroups':
        return {
          items: jobFamilyGroupsData?.data || [],
          totalPages: jobFamilyGroupsData?.totalPages || 0,
          totalRecords: jobFamilyGroupsData?.totalRecords || 0,
          isLoading: isFetchingJobFamilyGroups
        };
      case 'jobFamilies':
        return {
          items: jobFamiliesData?.data || [],
          totalPages: jobFamiliesData?.totalPages || 0,
          totalRecords: jobFamiliesData?.totalRecords || 0,
          isLoading: isFetchingJobFamilies
        };
      case 'targetPositions':
        return {
          items: targetPositionsData?.data || [],
          totalPages: targetPositionsData?.totalPages || 0,
          totalRecords: targetPositionsData?.totalRecords || 0,
          isLoading: isFetchingTargetPositions
        };
      default:
        return { items: [], totalPages: 0, totalRecords: 0, isLoading: false };
    }
  }, [
    modalType,
    courseType,
    modularCourses,
    programCourses,
    dacumPrograms,
    emergingNeedsCourses,
    businessUnitsData,
    jobFamilyGroupsData,
    jobFamiliesData,
    targetPositionsData,
    isFetchingModular,
    isFetchingProgram,
    isFetchingDacum,
    isFetchingEmergingNeeds,
    isFetchingBusinessUnits,
    isFetchingJobFamilyGroups,
    isFetchingJobFamilies,
    isFetchingTargetPositions
  ]);

  const {
    items: currentItems,
    totalPages,
    totalRecords,
    isLoading: isLoadingItems
  } = getCurrentData();

  /* eslint-disable */
  const availableItems = useMemo(() => {
    switch (modalType) {
      case 'courses':
        let transformedCourses: (CourseItem & { selected: boolean })[] = [];

        switch (courseType) {
          case 'modular':
            transformedCourses = (currentItems as CourseModularData[]).map((course) => ({
              courseId: course.msLCMSCourseID,
              courseName: course.courseName,
              courseCode: course.courseCode || '',
              deliveryType: course.deliveryType || 'modular',
              learningHours: course.learningHours || 0,
              category: course.courseCategory || '',
              description: course.description || '',
              modeOfDelivery: course.modeOfDelivery || '',
              businessUnits: (course as any).businessUnits || [],
              jobFamilyGroups: (course as any).jobFamilyGroups || [],
              jobFamilies: (course as any).jobFamilies || [],
              targetPositions: (course as any).targetPositions || [],
              selected: selectedCourseIds.has(course.msLCMSCourseID)
            }));
            break;
          case 'program':
            transformedCourses = (currentItems as CourseProgramData[]).map((course) => ({
              courseId: course.msLCMSCourseID,
              courseName: course.courseName,
              courseCode: course.courseCode || '',
              deliveryType: course.deliveryType || 'program',
              learningHours: course.learningHours || 0,
              category: course.courseCategory || '',
              description: course.description || '',
              modeOfDelivery: course.modeOfDelivery || '',
              businessUnits: (course as any).businessUnits || [],
              jobFamilyGroups: (course as any).jobFamilyGroups || [],
              jobFamilies: (course as any).jobFamilies || [],
              targetPositions: (course as any).targetPositions || [],
              selected: selectedCourseIds.has(course.msLCMSCourseID)
            }));
            break;
          case 'dacum':
            transformedCourses = (currentItems as DACUMProgramData[]).map((program) => ({
              courseId: program.msLCMSCourseID,
              courseName: program.courseName,
              courseCode: program.courseCode || '',
              deliveryType: 'dacum',
              learningHours: 0,
              category: 'DACUM Program',
              description: program.description || '',
              modeOfDelivery: '',
              businessUnits: (program as any).businessUnits || [],
              jobFamilyGroups: (program as any).jobFamilyGroups || [],
              jobFamilies: (program as any).jobFamilies || [],
              targetPositions: (program as any).targetPositions || [],
              selected: selectedCourseIds.has(program.msLCMSCourseID)
            }));
            break;
          case 'emerging needs':
            transformedCourses = (currentItems as CourseEmergingNeedsData[]).map((course) => ({
              courseId: course.msLCMSCourseID,
              courseName: course.courseName,
              courseCode: course.courseCode || '',
              deliveryType: course.deliveryType || 'emerging needs',
              learningHours: course.learningHours || 0,
              category: course.courseCategory || '',
              description: course.description || '',
              modeOfDelivery: course.modeOfDelivery || '',
              businessUnits: (course as any).businessUnits || [],
              jobFamilyGroups: (course as any).jobFamilyGroups || [],
              jobFamilies: (course as any).jobFamilies || [],
              targetPositions: (course as any).targetPositions || [],
              selected: selectedCourseIds.has(course.msLCMSCourseID)
            }));
            break;
        }
        return transformedCourses;

      case 'businessUnits':
        return (currentItems as any[])
          .map((bu) => ({
            ...bu,
            buCode: bu.buCode?.trim() || '',
            buName: bu.buName?.trim() || ''
          }))
          .filter((bu) => bu.buCode && bu.buName)
          .map((bu) => ({
            ...bu,
            selected: selectedBusinessUnitIds.has(bu.buCode)
          }));

      case 'jobFamilyGroups':
        return (currentItems as JobFamilyGroup[]).map((jfg) => ({
          ...jfg,
          selected: selectedJobFamilyGroupIds.has(jfg.jobFamilyGroupID)
        }));

      case 'jobFamilies':
        return (currentItems as JobFamily[]).map((jf) => ({
          ...jf,
          selected: selectedJobFamilyIds.has(jf.jobFamilyID)
        }));

      case 'targetPositions':
        return (currentItems as any[])
          .filter(
            (pos) =>
              !debounced ||
              getTargetPositionName(pos).toLowerCase().includes(debounced.toLowerCase())
          )
          .map((pos) => ({
            ...pos,
            positionId: pos.positionId || pos.positionID || pos.PositionID,
            positionName: getTargetPositionName(pos),
            namaJabatan: getTargetPositionName(pos),
            selected: selectedTargetPositionIds.has(getTargetPositionKey(pos))
          }));

      default:
        return [];
    }
  }, [
    modalType,
    courseType,
    currentItems,
    debounced,
    selectedCourseIds,
    selectedBusinessUnitIds,
    selectedJobFamilyGroupIds,
    selectedJobFamilyIds,
    selectedTargetPositionIds,
    getTargetPositionKey,
    getTargetPositionName
  ]);
  /* eslint-enable */

  const availableCourses = useMemo(() => {
    if (modalType === 'courses') {
      return availableItems as (CourseItem & { selected: boolean })[];
    }
    return [];
  }, [modalType, availableItems]);

  const availableBusinessUnits = useMemo(() => {
    if (modalType === 'businessUnits') {
      return availableItems as (BusinessUnit & { selected: boolean })[];
    }
    return [];
  }, [modalType, availableItems]);

  const availableJobFamilyGroups = useMemo(() => {
    if (modalType === 'jobFamilyGroups') {
      return availableItems as (JobFamilyGroup & { selected: boolean })[];
    }
    return [];
  }, [modalType, availableItems]);

  const availableJobFamilies = useMemo(() => {
    if (modalType === 'jobFamilies') {
      return (currentItems as JobFamily[]).map((jf) => ({
        ...jf,
        selected: selectedJobFamilyIds.has(jf.jobFamilyID)
      }));
    }
    return [];
  }, [modalType, currentItems, selectedJobFamilyIds]);

  const availableTargetPositions = useMemo(() => {
    if (modalType === 'targetPositions') {
      return availableItems as (any & { selected: boolean })[];
    }
    return [];
  }, [modalType, availableItems]);

  const canAddTargetPositions =
    businessUnits.length > 0 && jobFamilyGroups.length > 0 && jobFamilies.length > 0;

  const setTargetPositionsValue = useCallback(
    (positions: CoursePlanTargetPositionForm[]) => {
      setValue('targetPositions', positions, { shouldDirty: true, shouldTouch: true });
      setValue('targetPosition', positions.map((pos) => pos.positionName).join(', '), {
        shouldDirty: true,
        shouldTouch: true
      });
    },
    [setValue]
  );

  const filterTargetPositionsByDependencies = useCallback(
    (
      positions: CoursePlanTargetPositionForm[],
      nextBusinessUnits: CoursePlanBusinessUnitForm[],
      nextJobFamilyGroups: CoursePlanJobFamilyGroupForm[],
      nextJobFamilies: CoursePlanJobFamilyForm[]
    ) => {
      if (positions.length === 0) return positions;
      if (
        nextBusinessUnits.length === 0 ||
        nextJobFamilyGroups.length === 0 ||
        nextJobFamilies.length === 0
      ) {
        return [];
      }

      const nextBusinessUnitIds = new Set(nextBusinessUnits.map((bu) => bu.businessUnitId));
      const nextJobFamilyGroupIds = new Set(nextJobFamilyGroups.map((jfg) => jfg.jobFamilyGroupId));
      const nextJobFamilyIds = new Set(nextJobFamilies.map((jf) => jf.jobFamilyId));

      return positions.filter((position) => {
        const positionBusinessUnitIds = getTargetPositionBusinessUnitIds(position);
        const positionJobFamilyGroupId = getTargetPositionJobFamilyGroupId(position);
        const positionJobFamilyId = getTargetPositionJobFamilyId(position);

        return (
          positionBusinessUnitIds.some((id) => nextBusinessUnitIds.has(id)) &&
          nextJobFamilyGroupIds.has(positionJobFamilyGroupId) &&
          nextJobFamilyIds.has(positionJobFamilyId)
        );
      });
    },
    [
      getTargetPositionBusinessUnitIds,
      getTargetPositionJobFamilyGroupId,
      getTargetPositionJobFamilyId
    ]
  );

  const handleRemoveJobFamily = useCallback(
    (jobFamilyId: string) => {
      const updatedJobFamilies = jobFamilies.filter((jf) => jf.jobFamilyId !== jobFamilyId);
      setValue('jobFamilies', updatedJobFamilies, { shouldDirty: true, shouldTouch: true });
      setTargetPositionsValue(
        filterTargetPositionsByDependencies(
          targetPositions,
          businessUnits,
          jobFamilyGroups,
          updatedJobFamilies
        )
      );
    },
    [
      businessUnits,
      filterTargetPositionsByDependencies,
      jobFamilies,
      jobFamilyGroups,
      setTargetPositionsValue,
      setValue,
      targetPositions
    ]
  );

  const handleTargetPositionCheckbox = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, positionId: string) => {
      const { checked } = e.target;
      const position = availableTargetPositions.find(
        (pos) => getTargetPositionKey(pos) === positionId
      );

      if (!position) return;

      let updatedPositions: CoursePlanTargetPositionForm[];

      if (checked) {
        updatedPositions = [...targetPositions, buildTargetPositionFormValue(position)];
      } else {
        updatedPositions = targetPositions.filter(
          (pos) => getTargetPositionKey(pos) !== positionId
        );
      }

      setTargetPositionsValue(updatedPositions);
    },
    [
      targetPositions,
      availableTargetPositions,
      buildTargetPositionFormValue,
      getTargetPositionKey,
      setTargetPositionsValue
    ]
  );

  const handleJobFamilyCheckbox = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, jobFamilyId: string) => {
      const { checked } = e.target;
      const jobFamily = availableJobFamilies.find((jf) => jf.jobFamilyID === jobFamilyId);

      if (!jobFamily) return;

      // Find the corresponding job family group
      const selectedGroup = jobFamilyGroups.find(
        (jfg) => jfg.jobFamilyGroupId === selectedJobFamilyGroupForFamilies
      );

      if (!selectedGroup) return;

      let updatedJobFamilies: CoursePlanJobFamilyForm[];

      if (checked) {
        updatedJobFamilies = [
          ...jobFamilies,
          {
            jobFamilyId: jobFamily.jobFamilyID,
            jobFamilyName: jobFamily.jobFamilyName,
            jobFamilyGroupId: selectedGroup.jobFamilyGroupId,
            jobFamilyGroupName: selectedGroup.jobFamilyGroupName
          }
        ];
      } else {
        updatedJobFamilies = jobFamilies.filter((jf) => jf.jobFamilyId !== jobFamilyId);
      }

      setValue('jobFamilies', updatedJobFamilies, { shouldDirty: true, shouldTouch: true });
      setTargetPositionsValue(
        filterTargetPositionsByDependencies(
          targetPositions,
          businessUnits,
          jobFamilyGroups,
          updatedJobFamilies
        )
      );
    },
    [
      availableJobFamilies,
      businessUnits,
      filterTargetPositionsByDependencies,
      jobFamilies,
      jobFamilyGroups,
      selectedJobFamilyGroupForFamilies,
      setTargetPositionsValue,
      setValue,
      targetPositions
    ]
  );

  const handleCourseCheckbox = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, courseId: string) => {
      const { checked } = e.target;
      const course = availableCourses.find((c) => c.courseId === courseId);

      if (!course) return;

      let updatedCourses: CourseItem[];

      if (checked) {
        updatedCourses = [
          ...courses,
          {
            courseId: course.courseId,
            courseName: course.courseName,
            courseCode: course.courseCode,
            deliveryType: course.deliveryType,
            learningHours: course.learningHours,
            category: course.category,
            description: course.description,
            modeOfDelivery: course.modeOfDelivery || 'N/A'
          }
        ];
      } else {
        updatedCourses = courses.filter((c) => c.courseId !== courseId);
      }

      setValue('courses', updatedCourses, { shouldDirty: true, shouldTouch: true });
    },
    [availableCourses, courses, setValue]
  );

  const handleBusinessUnitCheckbox = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, buCode: string) => {
      const { checked } = e.target;
      const businessUnit = availableBusinessUnits.find((bu) => bu.buCode === buCode);

      if (!businessUnit?.buCode || !businessUnit?.buName) return;

      let updatedBusinessUnits: CoursePlanBusinessUnitForm[];

      if (checked) {
        updatedBusinessUnits = [
          ...businessUnits,
          {
            businessUnitId: businessUnit.buCode,
            businessUnitName: businessUnit.buName
          }
        ];
      } else {
        updatedBusinessUnits = businessUnits.filter((bu) => bu.businessUnitId !== buCode);
      }

      setValue('businessUnits', updatedBusinessUnits, { shouldDirty: true, shouldTouch: true });

      if (!checked) {
        const remainingBusinessUnitIds = updatedBusinessUnits.map((bu) => bu.businessUnitId);
        const updatedJobFamilyGroups = jobFamilyGroups.filter((jfg) => {
          if (!jfg.businessUnitIds || jfg.businessUnitIds.length === 0) {
            return true;
          }

          return jfg.businessUnitIds.some((id) => remainingBusinessUnitIds.includes(id));
        });
        const validJobFamilyGroupIds = new Set(
          updatedJobFamilyGroups.map((jfg) => jfg.jobFamilyGroupId)
        );
        const updatedJobFamilies = jobFamilies.filter((jf) =>
          validJobFamilyGroupIds.has(jf.jobFamilyGroupId)
        );

        setValue('jobFamilyGroups', updatedJobFamilyGroups, {
          shouldDirty: true,
          shouldTouch: true
        });
        setValue('jobFamilies', updatedJobFamilies, { shouldDirty: true, shouldTouch: true });
        setTargetPositionsValue(
          filterTargetPositionsByDependencies(
            targetPositions,
            updatedBusinessUnits,
            updatedJobFamilyGroups,
            updatedJobFamilies
          )
        );
      }
    },
    [
      availableBusinessUnits,
      businessUnits,
      filterTargetPositionsByDependencies,
      jobFamilies,
      jobFamilyGroups,
      setTargetPositionsValue,
      setValue,
      targetPositions
    ]
  );

  const handleJobFamilyGroupCheckbox = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, jobFamilyGroupId: string) => {
      const { checked } = e.target;
      const jobFamilyGroup = availableJobFamilyGroups.find(
        (jfg) => jfg.jobFamilyGroupID === jobFamilyGroupId
      );

      if (!jobFamilyGroup) return;

      let updatedJobFamilyGroups: CoursePlanJobFamilyGroupForm[];

      if (checked) {
        updatedJobFamilyGroups = [
          ...jobFamilyGroups,
          {
            jobFamilyGroupId: jobFamilyGroup.jobFamilyGroupID,
            jobFamilyGroupName: jobFamilyGroup.jobFamilyGroupName,
            businessUnitIds:
              jobFamilyGroup.businessUnitIds && jobFamilyGroup.businessUnitIds.length > 0
                ? jobFamilyGroup.businessUnitIds
                : selectedBusinessUnitIdsForQuery
          }
        ];
      } else {
        updatedJobFamilyGroups = jobFamilyGroups.filter(
          (jfg) => jfg.jobFamilyGroupId !== jobFamilyGroupId
        );
      }

      setValue('jobFamilyGroups', updatedJobFamilyGroups, { shouldDirty: true, shouldTouch: true });
      if (!checked) {
        const updatedJobFamilyGroupIds = new Set(
          updatedJobFamilyGroups.map((jfg) => jfg.jobFamilyGroupId)
        );
        const updatedJobFamilies = jobFamilies.filter((jf) =>
          updatedJobFamilyGroupIds.has(jf.jobFamilyGroupId)
        );

        setValue('jobFamilies', updatedJobFamilies, { shouldDirty: true, shouldTouch: true });
        setTargetPositionsValue(
          filterTargetPositionsByDependencies(
            targetPositions,
            businessUnits,
            updatedJobFamilyGroups,
            updatedJobFamilies
          )
        );
      }
    },
    [
      availableJobFamilyGroups,
      businessUnits,
      filterTargetPositionsByDependencies,
      jobFamilies,
      jobFamilyGroups,
      selectedBusinessUnitIdsForQuery,
      setTargetPositionsValue,
      setValue,
      targetPositions
    ]
  );

  // Handle removal functions
  const handleRemoveCourse = useCallback(
    (courseId: string) => {
      const updatedCourses = courses.filter((c) => c.courseId !== courseId);
      setValue('courses', updatedCourses, { shouldDirty: true, shouldTouch: true });
    },
    [courses, setValue]
  );

  const handleModeOfDeliveryChange = useCallback(
    (courseId: string, modeOfDelivery: string) => {
      const updatedCourses = courses.map((c) =>
        c.courseId === courseId ? { ...c, modeOfDelivery } : c
      );
      setValue('courses', updatedCourses, { shouldDirty: true, shouldTouch: true });
    },
    [courses, setValue]
  );

  const handleRemoveBusinessUnit = useCallback(
    (businessUnitId: string) => {
      const updatedBusinessUnits = businessUnits.filter(
        (bu) => bu.businessUnitId !== businessUnitId
      );
      const remainingBusinessUnitIds = updatedBusinessUnits.map((bu) => bu.businessUnitId);
      const updatedJobFamilyGroups = jobFamilyGroups.filter((jfg) => {
        if (!jfg.businessUnitIds || jfg.businessUnitIds.length === 0) {
          return true;
        }

        return jfg.businessUnitIds.some((id) => remainingBusinessUnitIds.includes(id));
      });
      const validJobFamilyGroupIds = new Set(
        updatedJobFamilyGroups.map((jfg) => jfg.jobFamilyGroupId)
      );
      const updatedJobFamilies = jobFamilies.filter((jf) =>
        validJobFamilyGroupIds.has(jf.jobFamilyGroupId)
      );

      setValue('businessUnits', updatedBusinessUnits, { shouldDirty: true, shouldTouch: true });
      setValue('jobFamilyGroups', updatedJobFamilyGroups, { shouldDirty: true, shouldTouch: true });
      setValue('jobFamilies', updatedJobFamilies, { shouldDirty: true, shouldTouch: true });
      setTargetPositionsValue(
        filterTargetPositionsByDependencies(
          targetPositions,
          updatedBusinessUnits,
          updatedJobFamilyGroups,
          updatedJobFamilies
        )
      );
    },
    [
      businessUnits,
      filterTargetPositionsByDependencies,
      jobFamilies,
      jobFamilyGroups,
      setTargetPositionsValue,
      setValue,
      targetPositions
    ]
  );

  const handleRemoveJobFamilyGroup = useCallback(
    (jobFamilyGroupId: string) => {
      const updatedJobFamilyGroups = jobFamilyGroups.filter(
        (jfg) => jfg.jobFamilyGroupId !== jobFamilyGroupId
      );
      const updatedJobFamilyGroupIds = new Set(
        updatedJobFamilyGroups.map((jfg) => jfg.jobFamilyGroupId)
      );
      const updatedJobFamilies = jobFamilies.filter((jf) =>
        updatedJobFamilyGroupIds.has(jf.jobFamilyGroupId)
      );

      setValue('jobFamilyGroups', updatedJobFamilyGroups, { shouldDirty: true, shouldTouch: true });
      setValue('jobFamilies', updatedJobFamilies, { shouldDirty: true, shouldTouch: true });
      setTargetPositionsValue(
        filterTargetPositionsByDependencies(
          targetPositions,
          businessUnits,
          updatedJobFamilyGroups,
          updatedJobFamilies
        )
      );
    },
    [
      businessUnits,
      filterTargetPositionsByDependencies,
      jobFamilies,
      jobFamilyGroups,
      setTargetPositionsValue,
      setValue,
      targetPositions
    ]
  );

  const handleClearBusinessUnits = useCallback(() => {
    setValue('businessUnits', [], { shouldDirty: true, shouldTouch: true });
    setValue('jobFamilyGroups', [], { shouldDirty: true, shouldTouch: true });
    setValue('jobFamilies', [], { shouldDirty: true, shouldTouch: true });
    setTargetPositionsValue([]);
  }, [setTargetPositionsValue, setValue]);

  const handleClearJobFamilyGroups = useCallback(() => {
    setValue('jobFamilyGroups', [], { shouldDirty: true, shouldTouch: true });
    setValue('jobFamilies', [], { shouldDirty: true, shouldTouch: true });
    setTargetPositionsValue([]);
  }, [setTargetPositionsValue, setValue]);

  const handleClearJobFamilies = useCallback(() => {
    setValue('jobFamilies', [], { shouldDirty: true, shouldTouch: true });
    setTargetPositionsValue([]);
  }, [setTargetPositionsValue, setValue]);

  // Handle pagination
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage((prev) => (prev === page ? prev : page));
  }, []);

  // Select-all handlers for each modal type (current page)
  const handleSelectAllCourses = useCallback(() => {
    const allSelected = availableCourses.every((c) => selectedCourseIds.has(c.courseId));
    if (allSelected) {
      const remaining = courses.filter(
        (c) => !availableCourses.some((a) => a.courseId === c.courseId)
      );
      setValue('courses', remaining, { shouldDirty: true, shouldTouch: true });
    } else {
      const newCourses = availableCourses
        .filter((c) => !selectedCourseIds.has(c.courseId))
        .map((c) => ({
          courseId: c.courseId,
          courseName: c.courseName,
          courseCode: c.courseCode,
          deliveryType: c.deliveryType,
          learningHours: c.learningHours,
          category: c.category,
          description: c.description,
          modeOfDelivery: c.modeOfDelivery || 'N/A'
        }));
      setValue('courses', [...courses, ...newCourses], { shouldDirty: true, shouldTouch: true });
    }
  }, [availableCourses, courses, selectedCourseIds, setValue]);

  const handleSelectAllBusinessUnits = useCallback(() => {
    const validBusinessUnits = availableBusinessUnits.filter((bu) => bu.buCode && bu.buName);
    const allSelected = validBusinessUnits.every((bu) => selectedBusinessUnitIds.has(bu.buCode));
    if (allSelected) {
      const remaining = businessUnits.filter(
        (bu) => !validBusinessUnits.some((a) => a.buCode === bu.businessUnitId)
      );
      const remainingBusinessUnitIds = remaining.map((bu) => bu.businessUnitId);
      const remainingJobFamilyGroups = jobFamilyGroups.filter((jfg) => {
        if (!jfg.businessUnitIds || jfg.businessUnitIds.length === 0) {
          return true;
        }

        return jfg.businessUnitIds.some((id) => remainingBusinessUnitIds.includes(id));
      });
      const validJobFamilyGroupIds = new Set(
        remainingJobFamilyGroups.map((jfg) => jfg.jobFamilyGroupId)
      );
      const remainingJobFamilies = jobFamilies.filter((jf) =>
        validJobFamilyGroupIds.has(jf.jobFamilyGroupId)
      );

      setValue('businessUnits', remaining, { shouldDirty: true, shouldTouch: true });
      setValue('jobFamilyGroups', remainingJobFamilyGroups, {
        shouldDirty: true,
        shouldTouch: true
      });
      setValue('jobFamilies', remainingJobFamilies, { shouldDirty: true, shouldTouch: true });
      setTargetPositionsValue(
        filterTargetPositionsByDependencies(
          targetPositions,
          remaining,
          remainingJobFamilyGroups,
          remainingJobFamilies
        )
      );
    } else {
      const newBUs = validBusinessUnits
        .filter((bu) => !selectedBusinessUnitIds.has(bu.buCode))
        .map((bu) => ({ businessUnitId: bu.buCode, businessUnitName: bu.buName }));
      setValue('businessUnits', [...businessUnits, ...newBUs], {
        shouldDirty: true,
        shouldTouch: true
      });
    }
  }, [
    availableBusinessUnits,
    businessUnits,
    filterTargetPositionsByDependencies,
    jobFamilyGroups,
    jobFamilies,
    selectedBusinessUnitIds,
    setTargetPositionsValue,
    targetPositions,
    setValue
  ]);

  const handleSelectAllJobFamilyGroups = useCallback(() => {
    const allSelected = availableJobFamilyGroups.every((jfg) =>
      selectedJobFamilyGroupIds.has(jfg.jobFamilyGroupID)
    );
    if (allSelected) {
      const remaining = jobFamilyGroups.filter(
        (jfg) => !availableJobFamilyGroups.some((a) => a.jobFamilyGroupID === jfg.jobFamilyGroupId)
      );
      const remainingJobFamilyGroupIds = new Set(remaining.map((jfg) => jfg.jobFamilyGroupId));
      const remainingJobFamilies = jobFamilies.filter((jf) =>
        remainingJobFamilyGroupIds.has(jf.jobFamilyGroupId)
      );

      setValue('jobFamilyGroups', remaining, { shouldDirty: true, shouldTouch: true });
      setValue('jobFamilies', remainingJobFamilies, { shouldDirty: true, shouldTouch: true });
      setTargetPositionsValue(
        filterTargetPositionsByDependencies(
          targetPositions,
          businessUnits,
          remaining,
          remainingJobFamilies
        )
      );
    } else {
      const newJFGs = availableJobFamilyGroups
        .filter((jfg) => !selectedJobFamilyGroupIds.has(jfg.jobFamilyGroupID))
        .map((jfg) => ({
          jobFamilyGroupId: jfg.jobFamilyGroupID,
          jobFamilyGroupName: jfg.jobFamilyGroupName,
          businessUnitIds:
            jfg.businessUnitIds && jfg.businessUnitIds.length > 0
              ? jfg.businessUnitIds
              : selectedBusinessUnitIdsForQuery
        }));
      setValue('jobFamilyGroups', [...jobFamilyGroups, ...newJFGs], {
        shouldDirty: true,
        shouldTouch: true
      });
    }
  }, [
    availableJobFamilyGroups,
    businessUnits,
    filterTargetPositionsByDependencies,
    jobFamilyGroups,
    jobFamilies,
    selectedJobFamilyGroupIds,
    selectedBusinessUnitIdsForQuery,
    setTargetPositionsValue,
    targetPositions,
    setValue
  ]);

  const handleSelectAllJobFamilies = useCallback(() => {
    const selectedGroup = jobFamilyGroups.find(
      (jfg) => jfg.jobFamilyGroupId === selectedJobFamilyGroupForFamilies
    );
    if (!selectedGroup) return;
    const allSelected = availableJobFamilies.every((jf) =>
      selectedJobFamilyIds.has(jf.jobFamilyID)
    );
    if (allSelected) {
      const remaining = jobFamilies.filter(
        (jf) => !availableJobFamilies.some((a) => a.jobFamilyID === jf.jobFamilyId)
      );
      setValue('jobFamilies', remaining, { shouldDirty: true, shouldTouch: true });
      setTargetPositionsValue(
        filterTargetPositionsByDependencies(
          targetPositions,
          businessUnits,
          jobFamilyGroups,
          remaining
        )
      );
    } else {
      const newJFs = availableJobFamilies
        .filter((jf) => !selectedJobFamilyIds.has(jf.jobFamilyID))
        .map((jf) => ({
          jobFamilyId: jf.jobFamilyID,
          jobFamilyName: jf.jobFamilyName,
          jobFamilyGroupId: selectedGroup.jobFamilyGroupId,
          jobFamilyGroupName: selectedGroup.jobFamilyGroupName
        }));
      setValue('jobFamilies', [...jobFamilies, ...newJFs], {
        shouldDirty: true,
        shouldTouch: true
      });
    }
  }, [
    availableJobFamilies,
    businessUnits,
    filterTargetPositionsByDependencies,
    jobFamilies,
    jobFamilyGroups,
    selectedJobFamilyIds,
    selectedJobFamilyGroupForFamilies,
    setTargetPositionsValue,
    targetPositions,
    setValue
  ]);

  const handleSelectAllTargetPositions = useCallback(() => {
    if (isLoadingItems) return;

    const allSelected = availableTargetPositions.every((pos) =>
      selectedTargetPositionIds.has(getTargetPositionKey(pos))
    );
    if (allSelected) {
      const remaining = targetPositions.filter(
        (pos) =>
          !availableTargetPositions.some(
            (a) => getTargetPositionKey(a) === getTargetPositionKey(pos)
          )
      );
      setTargetPositionsValue(remaining);
    } else {
      const newPositions = availableTargetPositions
        .filter((pos) => !selectedTargetPositionIds.has(getTargetPositionKey(pos)))
        .map(buildTargetPositionFormValue);
      const updated = [...targetPositions, ...newPositions];
      setTargetPositionsValue(updated);
    }
  }, [
    availableTargetPositions,
    targetPositions,
    selectedTargetPositionIds,
    isLoadingItems,
    buildTargetPositionFormValue,
    getTargetPositionKey,
    setTargetPositionsValue
  ]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateCoursePlanDto) => coursePlanService.createCoursePlan(data),
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'Course plan created successfully',
        color: 'green'
      });
      queryClient.invalidateQueries({ queryKey: ['coursePlans'] });
      navigate(pathnames.coursePlan);
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error?.message || 'Failed to create course plan',
        color: 'red'
      });
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: UpdateCoursePlanDto) => coursePlanService.updateCoursePlan(data),
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'Course plan updated successfully',
        color: 'green'
      });
      queryClient.invalidateQueries({ queryKey: ['coursePlans'] });
      queryClient.invalidateQueries({ queryKey: ['coursePlanDetail', id] });
      navigate(pathnames.coursePlan);
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error?.message || 'Failed to update course plan',
        color: 'red'
      });
    }
  });

  const onSubmit = useCallback(
    (data: CoursePlanFormData) => {
      setFormData(data);

      const payload = {
        planningName: data.planName,
        programType: data.programType as 'modular' | 'program' | 'emerging needs',
        planningDescription: data.description || '',
        planningStart: data.startTime,
        planningEnd: data.endTime,
        prerequisites: '',
        learningMaterials: '',
        facilitiesRequired: '',
        notes: data.notes || '',
        courses: data.courses.map((course) => ({
          courseId: course.courseId,
          modeOfDelivery: course.modeOfDelivery || 'N/A'
        })),
        targetPositions:
          data.targetPositions.length > 0
            ? data.targetPositions.map((position) => ({
                positionID: getTargetPositionKey(position),
                positionName: position.positionName
              }))
            : undefined,
        businessUnits:
          data.businessUnits.length > 0
            ? data.businessUnits.map((bu) => ({
                businessUnitID: bu.businessUnitId,
                businessUnitName: bu.businessUnitName
              }))
            : undefined,
        jobFamilyGroups:
          data.jobFamilyGroups.length > 0
            ? data.jobFamilyGroups.map((jfg) => ({
                jobFamilyGroupID: jfg.jobFamilyGroupId,
                jobFamilyGroupName: jfg.jobFamilyGroupName
              }))
            : undefined,
        jobFamilies:
          data.jobFamilies.length > 0
            ? data.jobFamilies.map((jf) => ({
                jobFamilyID: jf.jobFamilyId,
                jobFamilyName: jf.jobFamilyName,
                jobFamilyGroupID: jf.jobFamilyGroupId,
                jobFamilyGroupName: jf.jobFamilyGroupName
              }))
            : undefined
      };

      if (mode === 'edit' && data.courseId) {
        const updatePayload: UpdateCoursePlanDto = {
          msLCMSPlanningID: data.courseId,
          ...payload
        };
        updateMutation.mutate(updatePayload);
      } else {
        createMutation.mutate(payload);
      }
    },
    [mode, setFormData, createMutation, updateMutation]
  );

  const isFormLoading = createMutation.isPending || updateMutation.isPending;

  const courseModalColumns = useMemo(
    () => [
      {
        heading: 'Course Name',
        active: true,
        props: (row: CourseItem & { selected: boolean }) => (
          <div>
            <Text fw={500} size='sm'>
              {row.courseName}
            </Text>
            {row.description && (
              <Text size='xs' c='dimmed' mt={2} lineClamp={2}>
                {row.description}
              </Text>
            )}
          </div>
        )
      },
      {
        heading: 'Course Code',
        active: true,
        props: (row: CourseItem & { selected: boolean }) => (
          <Text size='sm'>{row.courseCode || '-'}</Text>
        )
      },
      {
        heading: 'Category',
        active: true,
        props: (row: CourseItem & { selected: boolean }) => (
          <Text size='sm'>{row.category || '-'}</Text>
        )
      },
      {
        heading: 'Mode of Delivery',
        active: true,
        props: (row: CourseItem & { selected: boolean }) => (
          <Badge size='sm' variant='light' color='blue'>
            {row.modeOfDelivery || 'N/A'}
          </Badge>
        )
      },
      {
        heading: 'Learning Hours',
        active: true,
        props: (row: CourseItem & { selected: boolean }) => (
          <Text size='sm' ta='center'>
            {row.learningHours && row.learningHours > 0 ? `${row.learningHours}h` : '-'}
          </Text>
        )
      },
      {
        heading: 'Select',
        active: true,
        props: (row: CourseItem & { selected: boolean }) => (
          <Checkbox
            checked={row.selected}
            onChange={(e) => handleCourseCheckbox(e, row.courseId)}
          />
        )
      }
    ],
    [handleCourseCheckbox]
  );

  const targetPositionModalColumns = useMemo(
    () => [
      {
        heading: 'Position Information',
        active: true,
        props: (row: any) => (
          <div>
            <Text fw={500} size='sm'>
              {getTargetPositionName(row)}
            </Text>
            <Text size='xs' c='dimmed'>
              ID: {getTargetPositionKey(row)}
            </Text>
          </div>
        )
      },
      {
        heading: 'Select',
        active: true,
        props: (row: any) => (
          <Checkbox
            checked={row.selected}
            onChange={(e) => handleTargetPositionCheckbox(e, getTargetPositionKey(row))}
          />
        )
      }
    ],
    [handleTargetPositionCheckbox, getTargetPositionKey, getTargetPositionName]
  );

  const businessUnitModalColumns = useMemo(
    () => [
      {
        heading: 'BU ID',
        active: true,
        props: (row: BusinessUnit & { selected: boolean }) => (
          <Text size='sm' fw={500}>
            {row.buCode}
          </Text>
        )
      },
      {
        heading: 'Business Unit Name',
        active: true,
        props: (row: BusinessUnit & { selected: boolean }) => <Text size='sm'>{row.buName}</Text>
      },
      {
        heading: 'Select',
        active: true,
        props: (row: BusinessUnit & { selected: boolean }) => (
          <Checkbox
            checked={row.selected}
            onChange={(e) => handleBusinessUnitCheckbox(e, row.buCode)}
          />
        )
      }
    ],
    [handleBusinessUnitCheckbox]
  );

  const jobFamilyGroupModalColumns = useMemo(
    () => [
      {
        heading: 'Job Family Group',
        active: true,
        props: (row: JobFamilyGroup & { selected: boolean }) => (
          <div>
            <Text size='sm' fw={500}>
              {row.jobFamilyGroupName}
            </Text>
            <Text size='xs' c='dimmed'>
              ID: {row.jobFamilyGroupID}
            </Text>
          </div>
        )
      },
      {
        heading: 'Select',
        active: true,
        props: (row: JobFamilyGroup & { selected: boolean }) => (
          <Checkbox
            checked={row.selected}
            onChange={(e) => handleJobFamilyGroupCheckbox(e, row.jobFamilyGroupID)}
          />
        )
      }
    ],
    [handleJobFamilyGroupCheckbox]
  );

  const jobFamilyModalColumns = useMemo(
    () => [
      {
        heading: 'Job Family Information',
        active: true,
        props: (row: JobFamily & { selected: boolean }) => (
          <div>
            <Text size='sm' fw={500}>
              {row.jobFamilyName}
            </Text>
            <Text size='xs' c='dimmed'>
              ID: {row.jobFamilyID}
            </Text>
          </div>
        )
      },
      {
        heading: 'Select',
        active: true,
        props: (row: JobFamily & { selected: boolean }) => (
          <Checkbox
            checked={row.selected}
            onChange={(e) => handleJobFamilyCheckbox(e, row.jobFamilyID)}
          />
        )
      }
    ],
    [handleJobFamilyCheckbox]
  );

  const getCurrentModalColumns = () => {
    switch (modalType) {
      case 'courses':
        return courseModalColumns;
      case 'businessUnits':
        return businessUnitModalColumns;
      case 'jobFamilyGroups':
        return jobFamilyGroupModalColumns;
      case 'jobFamilies':
        return jobFamilyModalColumns;
      case 'targetPositions':
        return targetPositionModalColumns;
      default:
        return [];
    }
  };

  const getCurrentModalData = () => {
    switch (modalType) {
      case 'courses':
        return availableCourses;
      case 'businessUnits':
        return availableBusinessUnits;
      case 'jobFamilyGroups':
        return availableJobFamilyGroups;
      case 'jobFamilies':
        return availableJobFamilies;
      case 'targetPositions':
        return availableTargetPositions;
      default:
        return [];
    }
  };

  // Selected items columns
  const selectedCoursesColumns = useMemo(
    () => [
      {
        heading: 'Course Name',
        active: true,
        props: (row: CourseItem) => (
          <div>
            <Text fw={500} size='sm'>
              {row.courseName}
            </Text>
            {row.description && (
              <Text size='xs' c='dimmed' mt={2} lineClamp={2}>
                {row.description}
              </Text>
            )}
          </div>
        )
      },
      {
        heading: 'Course ID',
        active: true,
        props: (row: CourseItem) => (
          <Text size='sm' c='dimmed'>
            {row.courseId || '-'}
          </Text>
        )
      },
      {
        heading: 'Course Code',
        active: true,
        props: (row: CourseItem) => <Text size='sm'>{row.courseCode || '-'}</Text>
      },
      {
        heading: 'Category',
        active: true,
        props: (row: CourseItem) => <Text size='sm'>{row.category || '-'}</Text>
      },
      {
        heading: 'Delivery Type',
        active: true,
        props: (row: CourseItem) => (
          <Badge variant='light' size='sm' color='blue'>
            {row.deliveryType || 'N/A'}
          </Badge>
        )
      },
      {
        heading: 'Mode of Delivery',
        active: true,
        props: (row: CourseItem) => (
          <Select
            size='xs'
            value={row.modeOfDelivery || 'N/A'}
            onChange={(value) => handleModeOfDeliveryChange(row.courseId, value || 'N/A')}
            data={MODE_OF_DELIVERY.map((mode) => ({
              value: mode.label,
              label: mode.label
            }))}
            disabled={mode === 'view'}
            style={{ minWidth: 180 }}
          />
        )
      },
      {
        heading: 'Learning Hours',
        active: true,
        props: (row: CourseItem) => (
          <Text size='sm' ta='center'>
            {row.learningHours && row.learningHours > 0 ? `${row.learningHours}h` : '-'}
          </Text>
        )
      },
      {
        heading: 'Actions',
        active: true,
        props: (row: CourseItem) => (
          <Tooltip label='Remove Course'>
            <ActionIcon
              variant='subtle'
              color='red'
              onClick={() => handleRemoveCourse(row.courseId)}
              disabled={mode === 'view'}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        )
      }
    ],
    [handleRemoveCourse, handleModeOfDeliveryChange, mode]
  );

  const courseTypeOptions = [
    { value: 'modular', label: 'Modular Courses' },
    { value: 'program', label: 'Program Courses' },
    { value: 'emerging needs', label: 'Emerging Needs' }
  ];

  const getModalTitle = () => {
    switch (modalType) {
      case 'courses':
        return `Add Courses (${selectedCourseIds.size} selected)`;
      case 'businessUnits':
        return `Add Business Units (${selectedBusinessUnitIds.size} selected)`;
      case 'jobFamilyGroups':
        return `Add Job Family Groups (${selectedJobFamilyGroupIds.size} selected)`;
      case 'jobFamilies':
        return `Add Job Families (${selectedJobFamilyIds.size} selected)`;
      case 'targetPositions':
        return `Add Target Positions (${selectedTargetPositionIds.size} selected)`;
      default:
        return 'Add Items';
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={32}>
          <Stack gap={16}>
            <Text c={'dark'} fw={600}>
              Basic Course Information
            </Text>
            <Controller
              name='planName'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Plan Name'
                  placeholder='Enter plan name'
                  error={errors.planName?.message}
                  disabled={mode === 'view'}
                  {...field}
                />
              )}
            />
            <Controller
              name='programType'
              control={control}
              render={({ field }) => (
                <SegmentedInput
                  label='Program Type'
                  options={['program', 'modular', 'emerging needs']}
                  error={errors.programType?.message}
                  value={field.value}
                  disabled={mode === 'view'}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              name='description'
              control={control}
              render={({ field }) => (
                <Textarea
                  label='Description'
                  placeholder='Enter course description'
                  error={errors.description?.message}
                  minRows={3}
                  disabled={mode === 'view'}
                  {...field}
                />
              )}
            />

            {/* Business Units Section */}
            <Box>
              <Text c={'dark'} size='sm' fw={500} mb={3}>
                Business Units
              </Text>

              {mode !== 'view' && (
                <Button
                  fullWidth
                  mb={8}
                  variant='outline'
                  leftSection={<IconPlus size={16} />}
                  onClick={() => openModal('businessUnits')}
                >
                  Add Business Units
                </Button>
              )}

              <BusinessUnitTable
                dataBusinessUnits={businessUnits}
                onRemove={handleRemoveBusinessUnit}
                mode={mode}
              />
            </Box>

            <Box>
              <Text c={'dark'} size='sm' fw={500} mb={3}>
                Job Family Groups
              </Text>

              {mode !== 'view' && (
                <Button
                  fullWidth
                  mb={8}
                  variant='outline'
                  leftSection={<IconPlus size={16} />}
                  onClick={() => {
                    if (businessUnits.length === 0) {
                      notifications.show({
                        title: 'Business Unit Required',
                        message: 'Please select at least one Business Unit first',
                        color: 'orange'
                      });
                      return;
                    }
                    openModal('jobFamilyGroups');
                  }}
                  disabled={businessUnits.length === 0}
                >
                  Add Job Family Groups
                </Button>
              )}

              <JobFamilyGroupTable
                dataJobFamilyGroups={jobFamilyGroups}
                onRemove={handleRemoveJobFamilyGroup}
                mode={mode}
              />
            </Box>

            {/* Job Families Section - NEW */}
            <Box>
              <Text c={'dark'} size='sm' fw={500} mb={3}>
                Job Families
              </Text>

              {mode !== 'view' && (
                <Button
                  fullWidth
                  mb={8}
                  variant='outline'
                  leftSection={<IconPlus size={16} />}
                  onClick={() => {
                    if (jobFamilyGroups.length === 0) {
                      notifications.show({
                        title: 'Job Family Group Required',
                        message: 'Please select at least one Job Family Group first',
                        color: 'orange'
                      });
                      return;
                    }
                    openModal('jobFamilies');
                  }}
                  disabled={jobFamilyGroups.length === 0}
                >
                  Add Job Families
                </Button>
              )}

              <JobFamilyTable
                dataJobFamilies={jobFamilies}
                onRemove={handleRemoveJobFamily}
                mode={mode}
              />
            </Box>

            <Box>
              <Text c={'dark'} size='sm' fw={500} mb={3}>
                Target Positions{' '}
                <Text span c='dimmed' size='xs'>
                  (Optional)
                </Text>
              </Text>

              {mode !== 'view' && (
                <Button
                  fullWidth
                  mb={8}
                  variant='outline'
                  leftSection={<IconPlus size={16} />}
                  disabled={!canAddTargetPositions}
                  onClick={() => openModal('targetPositions')}
                >
                  Add Target Positions
                </Button>
              )}

              {targetPositions.length > 0 ? (
                <Paper withBorder p={16} radius='lg'>
                  <Text fw={500} size='sm' mb={12}>
                    Target Positions ({targetPositions.length})
                  </Text>
                  <DataTables
                    data={targetPositions}
                    columns={[
                      {
                        heading: 'Position Name',
                        active: true,
                        props: (row: any) => (
                          <Text size='sm' fw={500}>
                            {row.positionName}
                          </Text>
                        )
                      },
                      {
                        heading: 'Actions',
                        active: true,
                        props: (row: any) => (
                          <Tooltip label='Remove Position'>
                            <ActionIcon
                              variant='subtle'
                              color='red'
                              onClick={() => {
                                const updatedPositions = targetPositions.filter(
                                  (pos) => pos.positionId !== row.positionId
                                );
                                setTargetPositionsValue(updatedPositions);
                              }}
                              disabled={mode === 'view'}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )
                      }
                    ]}
                    withPagination={false}
                    isNumbering={false}
                    loading={false}
                  />
                </Paper>
              ) : (
                <Text c='dimmed' size='sm' style={{ fontStyle: 'italic' }}>
                  {mode === 'view'
                    ? 'No target positions assigned to this plan'
                    : 'Click "Add Target Positions" to select positions (optional)'}
                </Text>
              )}
            </Box>

            {/* Courses Section */}
            <Box>
              <Text c={'dark'} size='sm' fw={500} mb={3}>
                Courses
              </Text>

              {mode !== 'view' && (
                <Button
                  fullWidth
                  mb={8}
                  variant='outline'
                  leftSection={<IconPlus size={16} />}
                  onClick={() => openModal('courses')}
                >
                  Add Courses
                </Button>
              )}

              {courses.length > 0 ? (
                <Paper withBorder p={16} radius='lg'>
                  <Text fw={500} size='sm' mb={12}>
                    Selected Courses ({courses.length})
                  </Text>
                  <DataTables
                    data={courses}
                    columns={selectedCoursesColumns}
                    withPagination={false}
                    isNumbering={false}
                    loading={false}
                  />
                </Paper>
              ) : (
                <Text c='dimmed' size='sm' style={{ fontStyle: 'italic' }}>
                  {mode === 'view'
                    ? 'No courses assigned to this plan'
                    : 'Click "Add Courses" to select courses'}
                </Text>
              )}
            </Box>
          </Stack>

          <Divider />

          <Stack gap={16}>
            <Text c={'dark'} fw={600}>
              Planning Details
            </Text>
            <Grid>
              <Grid.Col span={6}>
                <Controller
                  name='startTime'
                  control={control}
                  render={({ field }) => (
                    <DateField
                      label='Schedule Start Date'
                      error={errors.startTime?.message}
                      disabled={mode === 'view'}
                      value={parseLocalDate(field.value)}
                      onChange={(value) => field.onChange(toLocalDateString(value))}
                    />
                  )}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <Controller
                  name='endTime'
                  control={control}
                  render={({ field }) => (
                    <DateField
                      label='Schedule End Date'
                      error={errors.endTime?.message}
                      disabled={mode === 'view'}
                      value={parseLocalDate(field.value)}
                      onChange={(value) => field.onChange(toLocalDateString(value))}
                    />
                  )}
                />
              </Grid.Col>
            </Grid>
          </Stack>

          <Divider />

          <Stack gap={16}>
            <Text c={'dark'} fw={600}>
              Additional Notes
            </Text>
            <Controller
              name='notes'
              control={control}
              render={({ field }) => (
                <Textarea
                  label='Notes'
                  placeholder='Enter additional notes'
                  error={errors.notes?.message}
                  minRows={3}
                  disabled={mode === 'view'}
                  {...field}
                />
              )}
            />
          </Stack>

          <Group justify='flex-end'>
            <Button variant='outline' onClick={() => navigate(pathnames.coursePlan)}>
              {mode === 'view' ? 'Back' : 'Cancel'}
            </Button>
            {mode !== 'view' && (
              <Button
                type='submit'
                loading={isFormLoading}
                size='md'
                leftSection={<IconDeviceFloppy />}
                onClick={() => {
                  if (Object.keys(errors).length > 0) {
                    const errorFields = Object.keys(errors).join(', ');
                    notifications.show({
                      title: 'Validation Error',
                      message: `Please fill in the required fields: ${errorFields}`,
                      color: 'red'
                    });
                  }
                }}
              >
                {mode === 'add' ? 'Create' : 'Update'} Course Plan
              </Button>
            )}
            {mode === 'view' && (
              <Button
                size='md'
                onClick={() => {
                  setFormMode('edit');
                  navigate(`${pathnames.coursePlan}/edit/${id}`);
                }}
              >
                Edit Course Plan
              </Button>
            )}
          </Group>
        </Stack>
      </form>

      <Modal
        opened={opened}
        onClose={handleModalClose}
        title={getModalTitle()}
        size='xl'
        closeOnClickOutside={false}
        closeOnEscape={true}
      >
        <Paper p={16} radius='lg'>
          <Stack>
            <Group justify='space-between' align='flex-end'>
              {modalType === 'courses' && (
                <Select
                  label='Course Type'
                  value={courseType}
                  onChange={(value) => setCourseType(value as CourseType)}
                  data={courseTypeOptions}
                  style={{ minWidth: 200 }}
                />
              )}

              {modalType === 'jobFamilies' && (
                <Select
                  label='Select Job Family Group First'
                  placeholder='Choose a Job Family Group'
                  value={selectedJobFamilyGroupForFamilies}
                  onChange={(value) => {
                    setSelectedJobFamilyGroupForFamilies(value || '');
                    setCurrentPage(1);
                  }}
                  data={jobFamilyGroups.map((jfg) => ({
                    value: jfg.jobFamilyGroupId,
                    label: jfg.jobFamilyGroupName
                  }))}
                  style={{ minWidth: 300 }}
                  mb={16}
                />
              )}
              <TextInput
                leftSection={<IconSearch size={20} />}
                placeholder={`Search ${modalType}...`}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                style={{ flex: 1, maxWidth: 300 }}
              />
            </Group>

            {/* Active Filters Display for Business Units Modal */}
            {modalType === 'businessUnits' && businessUnits.length > 0 && (
              <Paper withBorder p={12} radius='md' bg='blue.0'>
                <Stack gap={8}>
                  <Group justify='space-between'>
                    <Text size='sm' fw={600} c='blue.9'>
                      Currently Selected:
                    </Text>
                    <Button
                      size='xs'
                      variant='subtle'
                      color='red'
                      onClick={handleClearBusinessUnits}
                    >
                      Clear All
                    </Button>
                  </Group>
                  <Group gap={8}>
                    {businessUnits.map((bu) => (
                      <Badge
                        key={bu.businessUnitId}
                        variant='light'
                        color='blue'
                        rightSection={
                          <ActionIcon
                            size='xs'
                            color='blue'
                            radius='xl'
                            variant='transparent'
                            onClick={() => handleRemoveBusinessUnit(bu.businessUnitId)}
                          >
                            <IconTrash size={12} />
                          </ActionIcon>
                        }
                      >
                        {bu.businessUnitName}
                      </Badge>
                    ))}
                  </Group>
                </Stack>
              </Paper>
            )}

            {/* Active Filters Display for Job Family Groups Modal */}
            {modalType === 'jobFamilyGroups' && jobFamilyGroups.length > 0 && (
              <Paper withBorder p={12} radius='md' bg='grape.0'>
                <Stack gap={8}>
                  <Group justify='space-between'>
                    <Text size='sm' fw={600} c='grape.9'>
                      Currently Selected:
                    </Text>
                    <Button
                      size='xs'
                      variant='subtle'
                      color='red'
                      onClick={handleClearJobFamilyGroups}
                    >
                      Clear All
                    </Button>
                  </Group>
                  <Group gap={8}>
                    {jobFamilyGroups.map((jfg) => (
                      <Badge
                        key={jfg.jobFamilyGroupId}
                        variant='light'
                        color='grape'
                        rightSection={
                          <ActionIcon
                            size='xs'
                            color='grape'
                            radius='xl'
                            variant='transparent'
                            onClick={() => handleRemoveJobFamilyGroup(jfg.jobFamilyGroupId)}
                          >
                            <IconTrash size={12} />
                          </ActionIcon>
                        }
                      >
                        {jfg.jobFamilyGroupName}
                      </Badge>
                    ))}
                  </Group>
                </Stack>
              </Paper>
            )}

            {/* Active Filters Display for Job Families Modal */}
            {modalType === 'jobFamilies' && jobFamilies.length > 0 && (
              <Paper withBorder p={12} radius='md' bg='violet.0'>
                <Stack gap={8}>
                  <Group justify='space-between'>
                    <Text size='sm' fw={600} c='violet.9'>
                      Currently Selected:
                    </Text>
                    <Button size='xs' variant='subtle' color='red' onClick={handleClearJobFamilies}>
                      Clear All
                    </Button>
                  </Group>
                  <Group gap={8}>
                    {jobFamilies.map((jf) => (
                      <Badge
                        key={jf.jobFamilyId}
                        variant='light'
                        color='violet'
                        rightSection={
                          <ActionIcon
                            size='xs'
                            color='violet'
                            radius='xl'
                            variant='transparent'
                            onClick={() => handleRemoveJobFamily(jf.jobFamilyId)}
                          >
                            <IconTrash size={12} />
                          </ActionIcon>
                        }
                      >
                        {jf.jobFamilyName}
                      </Badge>
                    ))}
                  </Group>
                </Stack>
              </Paper>
            )}

            {/* Active Filters Display for Target Positions Modal */}
            {modalType === 'targetPositions' &&
              (businessUnits.length > 0 ||
                jobFamilyGroups.length > 0 ||
                jobFamilies.length > 0) && (
                <Paper withBorder p={12} radius='md' bg='teal.0'>
                  <Stack gap={8}>
                    <Group justify='space-between'>
                      <Text size='sm' fw={600} c='teal.9'>
                        Active Filters:
                      </Text>
                      <Button
                        size='xs'
                        variant='subtle'
                        color='red'
                        onClick={handleClearBusinessUnits}
                      >
                        Clear All Filters
                      </Button>
                    </Group>

                    {businessUnits.length > 0 && (
                      <Box>
                        <Text size='xs' fw={500} c='dimmed' mb={4}>
                          Business Units:
                        </Text>
                        <Group gap={8}>
                          {businessUnits.map((bu) => (
                            <Badge
                              key={bu.businessUnitId}
                              variant='light'
                              color='blue'
                              rightSection={
                                <ActionIcon
                                  size='xs'
                                  color='blue'
                                  radius='xl'
                                  variant='transparent'
                                  onClick={() => handleRemoveBusinessUnit(bu.businessUnitId)}
                                >
                                  <IconTrash size={12} />
                                </ActionIcon>
                              }
                            >
                              {bu.businessUnitName}
                            </Badge>
                          ))}
                        </Group>
                      </Box>
                    )}

                    {jobFamilyGroups.length > 0 && (
                      <Box>
                        <Text size='xs' fw={500} c='dimmed' mb={4}>
                          Job Family Groups:
                        </Text>
                        <Group gap={8}>
                          {jobFamilyGroups.map((jfg) => (
                            <Badge
                              key={jfg.jobFamilyGroupId}
                              variant='light'
                              color='grape'
                              rightSection={
                                <ActionIcon
                                  size='xs'
                                  color='grape'
                                  radius='xl'
                                  variant='transparent'
                                  onClick={() => handleRemoveJobFamilyGroup(jfg.jobFamilyGroupId)}
                                >
                                  <IconTrash size={12} />
                                </ActionIcon>
                              }
                            >
                              {jfg.jobFamilyGroupName}
                            </Badge>
                          ))}
                        </Group>
                      </Box>
                    )}

                    {jobFamilies.length > 0 && (
                      <Box>
                        <Text size='xs' fw={500} c='dimmed' mb={4}>
                          Job Families:
                        </Text>
                        <Group gap={8}>
                          {jobFamilies.map((jf) => (
                            <Badge
                              key={jf.jobFamilyId}
                              variant='light'
                              color='violet'
                              rightSection={
                                <ActionIcon
                                  size='xs'
                                  color='violet'
                                  radius='xl'
                                  variant='transparent'
                                  onClick={() => handleRemoveJobFamily(jf.jobFamilyId)}
                                >
                                  <IconTrash size={12} />
                                </ActionIcon>
                              }
                            >
                              {jf.jobFamilyName}
                            </Badge>
                          ))}
                        </Group>
                      </Box>
                    )}
                  </Stack>
                </Paper>
              )}

            {/* Active Filters Display for Courses Modal */}
            {modalType === 'courses' &&
              (businessUnits.length > 0 ||
                jobFamilyGroups.length > 0 ||
                jobFamilies.length > 0 ||
                targetPositions.length > 0) && (
                <Paper withBorder p={12} radius='md' bg='blue.0'>
                  <Stack gap={8}>
                    <Group justify='space-between'>
                      <Text size='sm' fw={600} c='blue.9'>
                        Active Filters:
                      </Text>
                      <Button
                        size='xs'
                        variant='subtle'
                        color='red'
                        onClick={() => {
                          handleClearBusinessUnits();
                        }}
                      >
                        Clear All Filters
                      </Button>
                    </Group>

                    {businessUnits.length > 0 && (
                      <Box>
                        <Text size='xs' fw={500} c='dimmed' mb={4}>
                          Business Units:
                        </Text>
                        <Group gap={8}>
                          {businessUnits.map((bu) => (
                            <Badge
                              key={bu.businessUnitId}
                              variant='light'
                              color='blue'
                              rightSection={
                                <ActionIcon
                                  size='xs'
                                  color='blue'
                                  radius='xl'
                                  variant='transparent'
                                  onClick={() => handleRemoveBusinessUnit(bu.businessUnitId)}
                                >
                                  <IconTrash size={12} />
                                </ActionIcon>
                              }
                            >
                              {bu.businessUnitName}
                            </Badge>
                          ))}
                        </Group>
                      </Box>
                    )}

                    {jobFamilyGroups.length > 0 && (
                      <Box>
                        <Text size='xs' fw={500} c='dimmed' mb={4}>
                          Job Family Groups:
                        </Text>
                        <Group gap={8}>
                          {jobFamilyGroups.map((jfg) => (
                            <Badge
                              key={jfg.jobFamilyGroupId}
                              variant='light'
                              color='grape'
                              rightSection={
                                <ActionIcon
                                  size='xs'
                                  color='grape'
                                  radius='xl'
                                  variant='transparent'
                                  onClick={() => handleRemoveJobFamilyGroup(jfg.jobFamilyGroupId)}
                                >
                                  <IconTrash size={12} />
                                </ActionIcon>
                              }
                            >
                              {jfg.jobFamilyGroupName}
                            </Badge>
                          ))}
                        </Group>
                      </Box>
                    )}

                    {jobFamilies.length > 0 && (
                      <Box>
                        <Text size='xs' fw={500} c='dimmed' mb={4}>
                          Job Families:
                        </Text>
                        <Group gap={8}>
                          {jobFamilies.map((jf) => (
                            <Badge
                              key={jf.jobFamilyId}
                              variant='light'
                              color='violet'
                              rightSection={
                                <ActionIcon
                                  size='xs'
                                  color='violet'
                                  radius='xl'
                                  variant='transparent'
                                  onClick={() => handleRemoveJobFamily(jf.jobFamilyId)}
                                >
                                  <IconTrash size={12} />
                                </ActionIcon>
                              }
                            >
                              {jf.jobFamilyName}
                            </Badge>
                          ))}
                        </Group>
                      </Box>
                    )}

                    {targetPositions.length > 0 && (
                      <Box>
                        <Text size='xs' fw={500} c='dimmed' mb={4}>
                          Target Positions:
                        </Text>
                        <Group gap={8}>
                          {targetPositions.map((pos) => (
                            <Badge
                              key={pos.positionId}
                              variant='light'
                              color='teal'
                              rightSection={
                                <ActionIcon
                                  size='xs'
                                  color='teal'
                                  radius='xl'
                                  variant='transparent'
                                  onClick={() => {
                                    const updatedPositions = targetPositions.filter(
                                      (p) => p.positionId !== pos.positionId
                                    );
                                    setTargetPositionsValue(updatedPositions);
                                  }}
                                >
                                  <IconTrash size={12} />
                                </ActionIcon>
                              }
                            >
                              {pos.positionName}
                            </Badge>
                          ))}
                        </Group>
                      </Box>
                    )}
                  </Stack>
                </Paper>
              )}

            {totalRecords > 0 && (
              <Group justify='space-between'>
                <Text size='sm' c='dimmed'>
                  Showing {(currentPage - 1) * pageSize + 1} to{' '}
                  {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords} items
                </Text>
                <Text size='sm' c='dimmed'>
                  Page {currentPage} of {totalPages}
                </Text>
              </Group>
            )}

            {!isLoadingItems &&
              getCurrentModalData().length > 0 &&
              (() => {
                const handlers: Record<
                  string,
                  { allSelected: boolean; count: number; total: number; onToggle: () => void }
                > = {
                  courses: {
                    allSelected:
                      availableCourses.length > 0 &&
                      availableCourses.every((c) => selectedCourseIds.has(c.courseId)),
                    count: selectedCourseIds.size,
                    total: availableCourses.length,
                    onToggle: handleSelectAllCourses
                  },
                  businessUnits: {
                    allSelected:
                      availableBusinessUnits.length > 0 &&
                      availableBusinessUnits.every((bu) => selectedBusinessUnitIds.has(bu.buCode)),
                    count: selectedBusinessUnitIds.size,
                    total: availableBusinessUnits.length,
                    onToggle: handleSelectAllBusinessUnits
                  },
                  jobFamilyGroups: {
                    allSelected:
                      availableJobFamilyGroups.length > 0 &&
                      availableJobFamilyGroups.every((jfg) =>
                        selectedJobFamilyGroupIds.has(jfg.jobFamilyGroupID)
                      ),
                    count: selectedJobFamilyGroupIds.size,
                    total: availableJobFamilyGroups.length,
                    onToggle: handleSelectAllJobFamilyGroups
                  },
                  jobFamilies: {
                    allSelected:
                      availableJobFamilies.length > 0 &&
                      availableJobFamilies.every((jf) => selectedJobFamilyIds.has(jf.jobFamilyID)),
                    count: selectedJobFamilyIds.size,
                    total: availableJobFamilies.length,
                    onToggle: handleSelectAllJobFamilies
                  },
                  targetPositions: {
                    allSelected:
                      availableTargetPositions.length > 0 &&
                      availableTargetPositions.every((pos) =>
                        selectedTargetPositionIds.has(getTargetPositionKey(pos))
                      ),
                    count: selectedTargetPositionIds.size,
                    total: availableTargetPositions.length,
                    onToggle: handleSelectAllTargetPositions
                  }
                };
                const current = handlers[modalType];
                if (!current) return null;
                return (
                  <Group justify='space-between' align='center'>
                    <Checkbox
                      label={`Select all on this page (${current.total})`}
                      checked={current.allSelected}
                      indeterminate={
                        !current.allSelected &&
                        getCurrentModalData().some((row: any) => Boolean(row.selected))
                      }
                      onChange={current.onToggle}
                      disabled={isLoadingItems}
                    />
                    {current.count > 0 && (
                      <Text size='sm' c='dimmed'>
                        {current.count} selected
                      </Text>
                    )}
                  </Group>
                );
              })()}

            <DataTables
              key={`${modalType}-${currentPage}-${pageSize}`}
              data={getCurrentModalData()}
              columns={getCurrentModalColumns()}
              withPagination={false}
              loading={isLoadingItems}
              isNumbering={true}
              currentPage={currentPage}
              currentSize={pageSize}
              emptyMessageTitle={`No ${modalType} found`}
              emptyMessageDescription='Try adjusting your search criteria or filters'
            />

            {totalPages > 1 && (
              <Center>
                <Stack align='center' gap='xs'>
                  <Pagination
                    value={currentPage}
                    onChange={handlePageChange}
                    total={totalPages}
                    size='sm'
                    disabled={isLoadingItems}
                    withEdges
                    siblings={1}
                    boundaries={1}
                  />
                  <Text size='xs' c='dimmed'>
                    Total: {totalRecords} items, {totalPages} pages
                  </Text>
                </Stack>
              </Center>
            )}
          </Stack>
        </Paper>

        <Group justify='space-between' mt='md'>
          <Text size='sm' c='dimmed'>
            {(() => {
              switch (modalType) {
                case 'courses':
                  return (
                    selectedCourseIds.size > 0 &&
                    `${selectedCourseIds.size} course${selectedCourseIds.size !== 1 ? 's' : ''} selected`
                  );
                case 'businessUnits':
                  return (
                    selectedBusinessUnitIds.size > 0 &&
                    `${selectedBusinessUnitIds.size} business unit${selectedBusinessUnitIds.size !== 1 ? 's' : ''} selected`
                  );
                case 'jobFamilyGroups':
                  return (
                    selectedJobFamilyGroupIds.size > 0 &&
                    `${selectedJobFamilyGroupIds.size} job family group${selectedJobFamilyGroupIds.size !== 1 ? 's' : ''} selected`
                  );
                case 'jobFamilies':
                  return (
                    selectedJobFamilyIds.size > 0 &&
                    `${selectedJobFamilyIds.size} job family${selectedJobFamilyIds.size !== 1 ? 'families' : ''} selected`
                  );
                case 'targetPositions':
                  return (
                    selectedTargetPositionIds.size > 0 &&
                    `${selectedTargetPositionIds.size} target position${selectedTargetPositionIds.size !== 1 ? 's' : ''} selected`
                  );
                default:
                  return '';
              }
            })()}
          </Text>
          <Group>
            <Button variant='outline' onClick={handleModalClose}>
              Done
            </Button>
          </Group>
        </Group>
      </Modal>
    </>
  );
};

export default CoursePlanForm;
